// contexts/NotificationContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  NotificationService,
  Notification,
  getNotificationService,
} from "../Services/NotificationService";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => void;
  isConnected: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
  baseUrl: string;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  baseUrl,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [notificationService, setNotificationService] =
    useState<NotificationService | null>(null);

  useEffect(() => {
    // Initialize notification service
    const service = getNotificationService(baseUrl);
    setNotificationService(service);

    // Subscribe to notification updates
    const unsubscribe = service.subscribe((updatedNotifications) => {
      setNotifications(updatedNotifications);
      setUnreadCount(service.getUnreadCount());
    });

    // Start connection
    const initializeConnection = async () => {
      try {
        await service.startConnection();
        setIsConnected(true);

        // Request notification permission
        await NotificationService.requestNotificationPermission();
      } catch (error) {
        console.error("Failed to initialize notification connection:", error);
        setIsConnected(false);
      }
    };

    initializeConnection();

    // Cleanup
    return () => {
      unsubscribe();
      service.stopConnection();
    };
  }, [baseUrl]);

  const markAsRead = async (id: number): Promise<void> => {
    if (notificationService) {
      await notificationService.markAsRead(id);
    }
  };

  const markAllAsRead = (): void => {
    if (notificationService) {
      notificationService.markAllAsRead();
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        isConnected,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
